import { http, HttpResponse } from 'msw';

import { orchestratorEndpoint } from '../../lib';
import type { GetCurrentStepResponseDataT } from '../../orchestrator/loan-application/flow/get-current-step/types';
import type { OrchestratorResponseDataT } from '../../orchestrator/shared/types';
import { mockLoanOffer } from '../mock-helpers';

export const mockCurrentStepResponse: OrchestratorResponseDataT<GetCurrentStepResponseDataT> = {
  data: {
    flow_terminated: false,
    loan_offer: { response: { metadata: mockLoanOffer } },
    processing_request: false,
  },
  errors: [],
  flowUUID: 'a4a4b1e0-3c1a-4a7a-9a1a-3a1a4a4b1e03',
  route: 'display_loan_offer',
};

export const mockCurrentStepSmartBiddingResponse: OrchestratorResponseDataT<GetCurrentStepResponseDataT> = {
  data: {
    flow_terminated: false,
    loan_offer: { response: { metadata: mockLoanOffer } },
    processing_request: false,
    sb_lead: true,
  },
  errors: [],
  flowUUID: 'a4a4b1e0-3c1a-4a7a-9a1a-3a1a4a4b1e03',
  route: 'display_loan_offer',
};

// A flow that ended without a route this app knows how to render (e.g. terminated on a step
// that recorded no route metadata) and no loan_offer to fall back on — a 200 with nothing
// usable in it, as opposed to `error` below, which is a failed request.
export const mockDeadEndCurrentStepResponse: OrchestratorResponseDataT<GetCurrentStepResponseDataT> = {
  data: {
    flow_terminated: true,
    processing_request: false,
  },
  errors: [],
  flowUUID: 'a4a4b1e0-3c1a-4a7a-9a1a-3a1a4a4b1e03',
  route: null,
};

// A response carrying `errors` alongside a loan_offer in the same payload — processOrchestratorResponse's
// errors branch returns before ever writing that loan_offer into the store, so this must still
// count as a dead end even though the raw response itself is not missing one.
export const mockErrorsWithOfferCurrentStepResponse: OrchestratorResponseDataT<GetCurrentStepResponseDataT> = {
  data: {
    flow_terminated: false,
    loan_offer: { response: { metadata: mockLoanOffer } },
    processing_request: false,
  },
  errors: ['error_finding_flow'],
  flowUUID: 'a4a4b1e0-3c1a-4a7a-9a1a-3a1a4a4b1e03',
  route: 'display_loan_offer',
};

// MSW's own path-parameter syntax, not a real flowId — built directly via orchestratorEndpoint
// rather than apiEndpoints.currentStep(':flowId'), since that now percent-encodes its argument
// (`:flowId` -> `%3AflowId`), which would stop this pattern from matching any real request.
const currentStepPattern = orchestratorEndpoint('/orchestrator/:flowId/current_step');

export const currentStepHandlers = {
  deadEnd: http.get(
    currentStepPattern,
    () => HttpResponse.json(mockDeadEndCurrentStepResponse, { status: 200 }),
  ),
  // An immediate 500 rather than a delayed network error: deterministic, fast enough for
  // findBy* timeouts, and outside the 401/403 range that `refreshAndRetry` would turn into
  // a token refresh + logout.
  error: http.get(currentStepPattern, () => new HttpResponse(null, {
    status: 500,
    statusText: 'Current step lookup failure',
  })),
  errorsWithOffer: http.get(
    currentStepPattern,
    () => HttpResponse.json(mockErrorsWithOfferCurrentStepResponse, { status: 200 }),
  ),
  loading: http.get(currentStepPattern, () => new Promise(
    (resolve) => { setTimeout(() => resolve(HttpResponse.json({}, { status: 200 })), 30000); },
  )),
  notOnContract: http.get(currentStepPattern, () => new HttpResponse(null, {
    status: 410,
    statusText: 'Not on a contract',
  })),
  smartBidding: http.get(
    currentStepPattern,
    () => HttpResponse.json(mockCurrentStepSmartBiddingResponse, { status: 200 }),
  ),
  success: http.get(
    currentStepPattern,
    () => HttpResponse.json(mockCurrentStepResponse, { status: 200 }),
  ),
};
